import { supabase } from '../../config/supabase';
import { Contact, ContactCreateInput, ContactUpdateInput } from '../../types/contact';
import logger from '../../utils/logger';
import { authService } from './auth.service';

/**
 * Convertit un objet contact du format Supabase vers le format de l'application
 * @param data Données du contact au format Supabase
 * @returns Contact au format de l'application
 */
function toAppFormat(data: any): Contact {
  return {
    id: data.id,
    markerId: data.marker_id,
    gender: data.gender,
    name: data.name,
    firstName: data.first_name,
    address: data.address,
    postalCode: data.postal_code,
    city: data.city,
    phone: data.phone,
    email: data.email,
    notes: data.notes,
    designation: data.designation,
    createdAt: data.created_at ? new Date(data.created_at) : undefined,
    updatedAt: data.updated_at ? new Date(data.updated_at) : undefined
  };
}

/**
 * Convertit un objet contact du format de l'application vers le format Supabase
 * @param contactData Données du contact au format de l'application
 * @returns Données du contact au format Supabase
 */
function toSupabaseFormat(contactData: ContactCreateInput): Record<string, any> {
  return {
    marker_id: contactData.markerId,
    user_id: contactData.user_id,
    agency_id: contactData.agency_id,
    gender: contactData.gender,
    name: contactData.name,
    first_name: contactData.firstName,
    address: contactData.address,
    postal_code: contactData.postalCode,
    city: contactData.city,
    phone: contactData.phone,
    email: contactData.email,
    notes: contactData.notes,
    designation: contactData.designation || 'contact'
  };
}

/**
 * Convertit un objet contact du format de l'application vers le format Supabase pour mise à jour
 * @param contactData Données du contact au format de l'application
 * @returns Données du contact au format Supabase
 */
function toSupabaseUpdateFormat(contactData: ContactUpdateInput): Record<string, any> {
  const supabaseData: Record<string, any> = {};
  
  if (contactData.markerId !== undefined) supabaseData.marker_id = contactData.markerId;
  if (contactData.gender !== undefined) supabaseData.gender = contactData.gender;
  if (contactData.name !== undefined) supabaseData.name = contactData.name;
  if (contactData.firstName !== undefined) supabaseData.first_name = contactData.firstName;
  if (contactData.address !== undefined) supabaseData.address = contactData.address;
  if (contactData.postalCode !== undefined) supabaseData.postal_code = contactData.postalCode;
  if (contactData.city !== undefined) supabaseData.city = contactData.city;
  if (contactData.phone !== undefined) supabaseData.phone = contactData.phone;
  if (contactData.email !== undefined) supabaseData.email = contactData.email;
  if (contactData.notes !== undefined) supabaseData.notes = contactData.notes;
  if (contactData.designation !== undefined) supabaseData.designation = contactData.designation;

  return supabaseData;
}

/**
 * Service pour gérer les opérations CRUD sur les contacts dans Supabase
 */
export const contactsService = {
  /**
   * Récupère tous les contacts associés à un marqueur
   * @param markerId L'ID du marqueur
   * @returns Une promesse qui résout avec un tableau de contacts
   */
  async getByMarkerId(markerId: string): Promise<Contact[]> {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('marker_id', markerId);

      if (error) {
        logger.error('Erreur lors de la récupération des contacts:', error);
        throw error;
      }

      // Convertir les données de Supabase au format de l'application
      return (data || []).map(toAppFormat);
    } catch (error) {
      logger.error('Erreur lors de la récupération des contacts:', error);
      throw error;
    }
  },

  /**
   * Récupère un contact par son ID
   * @param id L'ID du contact à récupérer
   * @returns Une promesse qui résout avec le contact ou null si non trouvé
   */
  async getById(id: string): Promise<Contact | null> {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Code d'erreur pour "aucun résultat"
          return null;
        }
        logger.error(`Erreur lors de la récupération du contact ${id}:`, error);
        throw error;
      }

      if (!data) return null;

      // Convertir les données de Supabase au format de l'application
      return toAppFormat(data);
    } catch (error) {
      logger.error(`Erreur lors de la récupération du contact ${id}:`, error);
      throw error;
    }
  },

  /**
   * Crée un nouveau contact
   * @param contactData Les données du contact à créer
   * @returns Une promesse qui résout avec le contact créé
   */
  async create(contactData: ContactCreateInput): Promise<Contact> {
    try {
      // Récupérer l'utilisateur courant
      const { data: authData } = await supabase.auth.getUser();
      if (!authData || !authData.user) {
        throw new Error('Utilisateur non authentifié');
      }

      // Récupérer le profil utilisateur pour obtenir l'agency_id
      const { userProfile, error: profileError } = await authService.getUserProfile(authData.user.id);
      if (profileError) {
        throw profileError;
      }

      if (!userProfile || !userProfile.agency_id) {
        throw new Error('Profil utilisateur ou ID d\'agence non trouvé');
      }

      // Ajouter user_id et agency_id aux données du contact
      const contactWithIds: ContactCreateInput = {
        ...contactData,
        user_id: authData.user.id,
        agency_id: userProfile.agency_id
      };

      // Convertir les données de l'application au format de Supabase
      const supabaseData = toSupabaseFormat(contactWithIds);

      const { data, error } = await supabase
        .from('contacts')
        .insert(supabaseData)
        .select()
        .single();

      if (error) {
        logger.error('Erreur lors de la création du contact:', error);
        throw error;
      }
      
      if (!data) {
        logger.error('Aucune donnée retournée après la création du contact');
        throw new Error('Aucune donnée retournée après la création du contact');
      }

      // Convertir les données de Supabase au format de l'application
      return toAppFormat(data);
    } catch (error) {
      logger.error('Erreur lors de la création du contact:', error);
      throw error;
    }
  },

  /**
   * Met à jour un contact existant
   * @param id L'ID du contact à mettre à jour
   * @param contactData Les données à mettre à jour
   * @returns Une promesse qui résout avec le contact mis à jour
   */
  async update(id: string, contactData: ContactUpdateInput): Promise<Contact> {
    try {
      // Convertir les données de l'application au format de Supabase
      const supabaseData = toSupabaseUpdateFormat(contactData);

      const { data, error } = await supabase
        .from('contacts')
        .update(supabaseData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error(`Erreur lors de la mise à jour du contact ${id}:`, error);
        throw error;
      }
      
      if (!data) {
        logger.error(`Contact avec l'ID ${id} non trouvé`);
        throw new Error(`Contact avec l'ID ${id} non trouvé`);
      }

      // Convertir les données de Supabase au format de l'application
      return toAppFormat(data);
    } catch (error) {
      logger.error(`Erreur lors de la mise à jour du contact ${id}:`, error);
      throw error;
    }
  },

  /**
   * Supprime un contact
   * @param id L'ID du contact à supprimer
   * @returns Une promesse qui résout lorsque la suppression est terminée
   */
  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', id);

      if (error) {
        logger.error(`Erreur lors de la suppression du contact ${id}:`, error);
        throw error;
      }
    } catch (error) {
      logger.error(`Erreur lors de la suppression du contact ${id}:`, error);
      throw error;
    }
  },

  /**
   * Supprime tous les contacts associés à un marqueur
   * @param markerId L'ID du marqueur
   * @returns Une promesse qui résout lorsque la suppression est terminée
   */
  async deleteByMarkerId(markerId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('marker_id', markerId);

      if (error) {
        logger.error(`Erreur lors de la suppression des contacts du marqueur ${markerId}:`, error);
        throw error;
      }
    } catch (error) {
      logger.error(`Erreur lors de la suppression des contacts du marqueur ${markerId}:`, error);
      throw error;
    }
  }
};

